import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cmdbApi, credentialsApi } from '@/api/clients/apiClient'
import { useOrgStore } from '@/stores/orgStore'
import type {
  CmdbHandler,
  CmdbAuthType,
  CreateHandlerRequest,
  UpdateHandlerRequest,
  FileExchangeConfig,
  FileTransport,
  FileFormat,
  CredentialType,
} from '@/api/types'

// ── Profile selector helper ───────────────────────────────────────────────────
// Loaded inside the modal so it always has fresh data

function useProfiles(orgId: string) {
  const { data: inbound } = useQuery({
    queryKey: ['cmdb-inbound-profiles', orgId],
    queryFn: () => cmdbApi.listProfiles(orgId, 'inbound'),
    enabled: !!orgId,
  })
  const { data: outbound } = useQuery({
    queryKey: ['cmdb-outbound-profiles', orgId],
    queryFn: () => cmdbApi.listProfiles(orgId, 'outbound'),
    enabled: !!orgId,
  })
  return {
    inboundProfiles: inbound ?? [],
    outboundProfiles: outbound ?? [],
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusBadge(status: CmdbHandler['status']) {
  const map = {
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    inactive: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    error: 'bg-red-100 text-error dark:bg-red-900/30 dark:text-red-400',
  }
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${map[status]}`}>
      {status}
    </span>
  )
}

function exchangeBadge(t: string) {
  const cls = t === 'file'
    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
    : 'bg-primary/10 text-primary'
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{t}</span>
  )
}

// ── Handler form state ──────────────────────────────────────────────────────

const defaultForm = (): CreateHandlerRequest => ({
  name: '',
  direction: 'inbound',
  exchange_type: 'api',
  instance_url: '',
  auth_type: 'oauth2_client',
  ci_table: 'cmdb_ci',
  external_id_field: 'sys_id',
  ci_class_filter: '',
  event_subscription_enabled: false,
  poll_interval_minutes: 360,
  max_api_calls_per_minute: 60,
})

const defaultFileExchange = (): FileExchangeConfig => ({
  file_transport: 'sftp',
  file_format: 'csv',
  inbound_path: '',
  outbound_path: '',
})

// ── HandlerModal ────────────────────────────────────────────────────────────

interface HandlerModalProps {
  handler?: CmdbHandler
  onClose: () => void
}

export function HandlerModal({ handler, onClose }: HandlerModalProps) {
  const { currentOrgId: orgId } = useOrgStore()
  const queryClient = useQueryClient()
  const isEdit = !!handler
  const { inboundProfiles, outboundProfiles } = useProfiles(orgId)
  const { data: adapterTemplates = [] } = useQuery({
    queryKey: ['cmdb-adapter-templates', orgId],
    queryFn: () => cmdbApi.listAdapterTemplates(orgId),
    enabled: !!orgId,
  })
  const [selectedTemplate, setSelectedTemplate] = useState('')

  const [form, setForm] = useState<CreateHandlerRequest>(
    handler
      ? {
          name: handler.name,
          direction: handler.direction,
          exchange_type: handler.exchange_type,
          instance_url: handler.instance_url,
          auth_type: handler.auth_type,
          ci_table: handler.ci_table,
          external_id_field: handler.external_id_field,
          ci_class_filter: handler.ci_class_filter ?? '',
          event_subscription_enabled: handler.event_subscription_enabled,
          poll_interval_minutes: handler.poll_interval_minutes,
          max_api_calls_per_minute: handler.max_api_calls_per_minute,
          file_exchange_config: handler.file_exchange_config,
          profile_id: handler.profile_id,
        }
      : defaultForm()
  )

  const [fileExchange, setFileExchange] = useState<FileExchangeConfig>(
    handler?.file_exchange_config ?? defaultFileExchange()
  )
  const [customConfig, setCustomConfig] = useState(
    handler?.custom_adapter_config ? JSON.stringify(handler.custom_adapter_config, null, 2) : '{}'
  )
  const [credMode, setCredMode] = useState<'select' | 'new'>('select')
  const [selectedCredId, setSelectedCredId] = useState('')
  const [newCredName, setNewCredName] = useState('')
  const [newCredType, setNewCredType] = useState<CredentialType>('token')
  const [newCredData, setNewCredData] = useState('')
  const [newCredHint, setNewCredHint] = useState('')
  const [error, setError] = useState('')

  const { data: existingCreds = [] } = useQuery({
    queryKey: ['credentials', orgId],
    queryFn: () => credentialsApi.list(orgId),
    enabled: !!orgId,
  })

  useEffect(() => {
    if (form.profile_id) return
    const profiles = form.direction === 'inbound' ? inboundProfiles : outboundProfiles
    const generic = profiles.find((p) => p.name === 'Generic' && p.is_system)
    if (generic) setForm((f) => ({ ...f, profile_id: generic.id }))
  }, [inboundProfiles, outboundProfiles, form.direction, form.profile_id])

  const mutation = useMutation({
    mutationFn: (req: CreateHandlerRequest | UpdateHandlerRequest) =>
      isEdit
        ? cmdbApi.updateHandler(orgId, handler!.id, req as UpdateHandlerRequest)
        : cmdbApi.createHandler(orgId, req as CreateHandlerRequest),
    onSuccess: async (result) => {
      if (!isEdit) {
        try {
          if (selectedCredId) {
            await cmdbApi.setCredential(orgId, result.id, { credential_id: selectedCredId })
          } else if (newCredData.trim()) {
            const newCred = await credentialsApi.create(orgId, {
              name: newCredName.trim() || `${form.name} Credential`,
              credential_type: newCredType,
              data: newCredData,
              hint: newCredHint,
            })
            await cmdbApi.setCredential(orgId, result.id, { credential_id: newCred.id })
          }
          queryClient.invalidateQueries({ queryKey: ['credentials', orgId] })
        } catch {
          // non-fatal: handler created, credential set can be retried from detail page
        }
      }
      queryClient.invalidateQueries({ queryKey: ['cmdb-handlers', orgId] })
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  const set = (key: keyof CreateHandlerRequest) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }))

  const setFx = (key: keyof FileExchangeConfig) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setFileExchange((f) => ({ ...f, [key]: e.target.value }))

  const inp = (val: string, onChange: React.ChangeEventHandler<HTMLInputElement>, ph = '') => (
    <input
      value={val}
      onChange={onChange}
      placeholder={ph}
      className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
    />
  )

  const sel = (
    val: string,
    onChange: React.ChangeEventHandler<HTMLSelectElement>,
    options: { value: string; label: string }[]
  ) => (
    <select
      value={val}
      onChange={onChange}
      className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  )

  const lbl = (text: string) => (
    <label className="block text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">{text}</label>
  )

  const handleSubmit = () => {
    if (!form.name.trim()) return setError('Name required')
    if (form.exchange_type !== 'file' && !form.instance_url?.trim()) return setError('Instance URL required')

    let payload: CreateHandlerRequest = { ...form }
    if (form.exchange_type === 'file') {
      payload = { ...payload, file_exchange_config: fileExchange, instance_url: '' }
    } else {
      try {
        payload = { ...payload, custom_adapter_config: JSON.parse(customConfig) }
      } catch {
        return setError('Custom adapter config must be valid JSON')
      }
    }

    setError('')
    mutation.mutate(payload)
  }

  const authOptions: { value: CmdbAuthType; label: string }[] = [
    { value: 'oauth2_client', label: 'OAuth2 Client Credentials' },
    { value: 'api_key', label: 'API Key' },
    { value: 'basic', label: 'Basic Auth' },
    { value: 'mtls', label: 'mTLS' },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-surface-light dark:bg-[#0d1b2a] rounded-2xl shadow-float flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-light dark:border-border-dark shrink-0">
          <h2 className="text-base font-semibold text-text-light dark:text-text-dark">
            {isEdit ? 'Edit Handler' : 'New CMDB Handler'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-primary/5 transition-colors">
            <span className="material-icons text-subtle-light dark:text-subtle-dark text-xl">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          <div>
            {lbl('Handler Name')}
            {inp(form.name, set('name'), 'e.g. ServiceNow Production')}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              {lbl('Direction')}
              {sel(form.direction, (e) => setForm((f) => ({ ...f, direction: e.target.value, profile_id: undefined })), [
                { value: 'inbound', label: 'Inbound' },
                { value: 'outbound', label: 'Outbound' },
              ])}
            </div>
            <div>
              {lbl('Connection Type')}
              {sel(
                form.exchange_type,
                (e) => {
                  setForm((f) => ({ ...f, exchange_type: e.target.value }))
                  if (e.target.value === 'file') {
                    setCustomConfig('{}')
                    setSelectedTemplate('')
                  }
                },
                [
                  { value: 'api', label: 'API' },
                  { value: 'file', label: 'File Exchange' },
                ]
              )}
            </div>
          </div>

          {form.exchange_type !== 'file' && (
            <div>
              {lbl('Instance URL')}
              {inp(form.instance_url ?? '', set('instance_url'), 'https://instance.service-now.com')}
            </div>
          )}

          {form.exchange_type !== 'file' && (
            <div>
              {lbl('Auth Type')}
              {sel(form.auth_type ?? 'oauth2_client', (e) => setForm((f) => ({ ...f, auth_type: e.target.value as CmdbAuthType })), authOptions)}
            </div>
          )}

          {form.exchange_type !== 'file' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                {lbl('CI Table')}
                {inp(form.ci_table ?? '', set('ci_table'), 'cmdb_ci')}
              </div>
              <div>
                {lbl('External ID Field')}
                {inp(form.external_id_field ?? '', set('external_id_field'), 'sys_id')}
              </div>
            </div>
          )}

          {form.exchange_type !== 'file' && (
            <div>
              {lbl('CI Class Filter (optional)')}
              {inp(form.ci_class_filter ?? '', set('ci_class_filter'), 'e.g. cmdb_ci_appl')}
            </div>
          )}

          {/* File exchange config */}
          {form.exchange_type === 'file' && (
            <div className="space-y-3 rounded-xl p-4">
              <p className="text-xs font-semibold text-text-light dark:text-text-dark">File Exchange Config</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  {lbl('Transport')}
                  {sel(fileExchange.file_transport, (e) => setFx('file_transport')(e as React.ChangeEvent<HTMLSelectElement>), [
                    { value: 'sftp', label: 'SFTP' },
                    { value: 's3', label: 'S3 / Compatible' },
                    { value: 'local', label: 'Local Filesystem' },
                  ] as { value: FileTransport; label: string }[])}
                </div>
                <div>
                  {lbl('File Format')}
                  {sel(fileExchange.file_format, (e) => setFx('file_format')(e as React.ChangeEvent<HTMLSelectElement>), [
                    { value: 'csv', label: 'CSV' },
                    { value: 'json', label: 'JSON' },
                    { value: 'xml', label: 'XML' },
                    { value: 'xlsx', label: 'XLSX' },
                  ] as { value: FileFormat; label: string }[])}
                </div>
              </div>
              <div>
                {lbl('Path')}
                {inp(
                  fileExchange.inbound_path || fileExchange.outbound_path,
                  (e) => setFileExchange((f) => ({ ...f, inbound_path: e.target.value, outbound_path: e.target.value })),
                  '/data/ci/'
                )}
              </div>
              {fileExchange.file_transport === 'sftp' && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    {lbl('SFTP Host')}
                    {inp(fileExchange.sftp_host ?? '', (e) => setFileExchange((f) => ({ ...f, sftp_host: e.target.value })), 'sftp.example.com')}
                  </div>
                  <div>
                    {lbl('SFTP Port')}
                    <input
                      type="number"
                      value={fileExchange.sftp_port ?? 22}
                      onChange={(e) => setFileExchange((f) => ({ ...f, sftp_port: Number(e.target.value) }))}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                  <div className="col-span-3">
                    {lbl('SFTP User')}
                    {inp(fileExchange.sftp_user ?? '', (e) => setFileExchange((f) => ({ ...f, sftp_user: e.target.value })), 'ftpuser')}
                  </div>
                </div>
              )}
              {fileExchange.file_transport === 's3' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      {lbl('S3 Bucket')}
                      {inp(fileExchange.s3_bucket ?? '', (e) => setFileExchange((f) => ({ ...f, s3_bucket: e.target.value })), 'my-bucket')}
                    </div>
                    <div>
                      {lbl('S3 Region')}
                      {inp(fileExchange.s3_region ?? '', (e) => setFileExchange((f) => ({ ...f, s3_region: e.target.value })), 'ap-southeast-1')}
                    </div>
                  </div>
                  <div>
                    {lbl('S3 Prefix (optional)')}
                    {inp(fileExchange.s3_prefix ?? '', (e) => setFileExchange((f) => ({ ...f, s3_prefix: e.target.value })), 'pqc/ci/')}
                  </div>
                  <div>
                    {lbl('S3 Endpoint URL (optional)')}
                    {inp(fileExchange.s3_endpoint_url ?? '', (e) => setFileExchange((f) => ({ ...f, s3_endpoint_url: e.target.value })), 'https://s3.compatible.endpoint')}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  {lbl('Outbound Flush Interval (min)')}
                  <input
                    type="number"
                    value={fileExchange.outbound_flush_interval_minutes ?? 15}
                    onChange={(e) => setFileExchange((f) => ({ ...f, outbound_flush_interval_minutes: Number(e.target.value) }))}
                    min={1}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
                <div>
                  {lbl('Transfer Timeout (sec)')}
                  <input
                    type="number"
                    value={fileExchange.transfer_timeout_seconds ?? 120}
                    onChange={(e) => setFileExchange((f) => ({ ...f, transfer_timeout_seconds: Number(e.target.value) }))}
                    min={1}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="checksum_val"
                  checked={fileExchange.checksum_validation ?? false}
                  onChange={(e) => setFileExchange((f) => ({ ...f, checksum_validation: e.target.checked }))}
                  className="w-4 h-4 rounded border border-border-light dark:border-border-dark accent-primary dark:accent-secondary bg-white dark:bg-[#0d1b2a]"
                />
                <label htmlFor="checksum_val" className="text-sm text-text-light dark:text-text-dark">
                  Validate SHA-256 checksum sidecar on inbound
                </label>
              </div>
            </div>
          )}

          {/* Adapter template picker */}
          {form.exchange_type !== 'file' && adapterTemplates.length > 0 && (
            <div>
              {lbl('Load Template')}
              <select
                value={selectedTemplate}
                onChange={(e) => {
                  const name = e.target.value
                  setSelectedTemplate(name)
                  const tpl = adapterTemplates.find((t) => t.name === name)
                  if (!tpl) return
                  setCustomConfig(JSON.stringify(tpl.custom_adapter_config, null, 2))
                  if (tpl.auth_types.length > 0) {
                    setForm((f) => ({ ...f, auth_type: tpl.auth_types[0] as CmdbAuthType }))
                  }
                }}
                className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="" disabled>Select a template…</option>
                {adapterTemplates.map((t) => (
                  <option key={t.name} value={t.name} title={t.description}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Custom adapter config */}
          {form.exchange_type !== 'file' && (
            <div>
              {lbl('Custom Adapter Config (JSON)')}
              <textarea
                value={customConfig}
                onChange={(e) => setCustomConfig(e.target.value)}
                rows={5}
                className="w-full px-3 py-2 text-xs font-mono rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                placeholder='{"base_url":"...","ci_fetch_path":"/api/ci","ci_write_path":"/api/ci/{id}"}'
              />
            </div>
          )}

          {!isEdit && (
            <div>
              {lbl('Credential (optional)')}
              <div className="flex border-b border-border-light dark:border-border-dark mb-3">
                {(['select', 'new'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setCredMode(m)}
                    className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                      credMode === m
                        ? 'text-primary border-b-2 border-primary'
                        : 'text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
                    }`}
                  >
                    {m === 'select' ? 'Select Existing' : 'Create New'}
                  </button>
                ))}
              </div>

              {credMode === 'select' ? (
                existingCreds.length === 0 ? (
                  <p className="text-xs text-subtle-light dark:text-subtle-dark py-1">
                    No credentials yet. Switch to "Create New" to create one.
                  </p>
                ) : (
                  <select
                    value={selectedCredId}
                    onChange={(e) => setSelectedCredId(e.target.value)}
                    className="w-full pl-3 pr-8 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark"
                  >
                    <option value="">— select credential —</option>
                    {existingCreds.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} · {c.credential_type}{c.hint ? ` (${c.hint})` : ''}
                      </option>
                    ))}
                  </select>
                )
              ) : (
                <div className="space-y-2">
                  <div>
                    {lbl('Name')}
                    <input
                      value={newCredName}
                      onChange={(e) => setNewCredName(e.target.value)}
                      placeholder={form.name ? `${form.name} Credential` : 'Credential name'}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                  <div>
                    {lbl('Type')}
                    <select
                      value={newCredType}
                      onChange={(e) => setNewCredType(e.target.value as CredentialType)}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark"
                    >
                      <option value="token">Token</option>
                      <option value="ssh-key">SSH Key</option>
                      <option value="basic">Basic (user:pass)</option>
                      <option value="iam-role">IAM Role</option>
                    </select>
                  </div>
                  <div>
                    {lbl('Data')}
                    <input
                      type="password"
                      value={newCredData}
                      onChange={(e) => setNewCredData(e.target.value)}
                      placeholder="api_key / clientId:clientSecret / user:pass"
                      className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                  <div>
                    {lbl('Hint (optional)')}
                    <input
                      value={newCredHint}
                      onChange={(e) => setNewCredHint(e.target.value)}
                      placeholder="e.g. prod-api-key"
                      className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              {lbl('Poll Interval (minutes)')}
              <input
                type="number"
                value={form.poll_interval_minutes}
                onChange={(e) => setForm((f) => ({ ...f, poll_interval_minutes: Number(e.target.value) }))}
                min={1}
                className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div>
              {lbl('Max API Calls/min')}
              <input
                type="number"
                value={form.max_api_calls_per_minute}
                onChange={(e) => setForm((f) => ({ ...f, max_api_calls_per_minute: Number(e.target.value) }))}
                min={1}
                className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="event_sub"
              checked={form.event_subscription_enabled}
              onChange={(e) => setForm((f) => ({ ...f, event_subscription_enabled: e.target.checked }))}
              className="w-4 h-4 rounded border border-border-light dark:border-border-dark accent-primary dark:accent-secondary bg-white dark:bg-[#0d1b2a]"
            />
            <label htmlFor="event_sub" className="text-sm text-text-light dark:text-text-dark">
              Enable webhook event subscription
            </label>
          </div>

          {/* Default mapping profile */}
          <div className="space-y-3 rounded-xl p-4">
            <p className="text-xs font-semibold text-text-light dark:text-text-dark">Mapping Profile</p>
            <p className="text-xs text-subtle-light dark:text-subtle-dark -mt-1">
              Applied to all syncs unless overridden per-request.
            </p>
            <div>
              {lbl('Profile')}
              <select
                value={form.profile_id ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, profile_id: e.target.value || undefined }))}
                className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                {(form.direction === 'inbound' ? inboundProfiles : outboundProfiles).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.is_system ? ' (system)' : ''}</option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <span className="material-icons text-error text-base">error_outline</span>
              <p className="text-sm text-error">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border-light dark:border-border-dark shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-primary/5 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Handler'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── CmdbHandlers (page) ─────────────────────────────────────────────────────

export default function CmdbHandlers({
  asTab = false,
  showCreate: externalShowCreate,
  onShowCreateChange,
}: {
  asTab?: boolean
  showCreate?: boolean
  onShowCreateChange?: (v: boolean) => void
} = {}) {
  const { currentOrgId: orgId } = useOrgStore()
  const queryClient = useQueryClient()
  const [localShowCreate, setLocalShowCreate] = useState(false)
  const isCreating = asTab && externalShowCreate !== undefined ? externalShowCreate : localShowCreate
  const setIsCreating = asTab && onShowCreateChange ? onShowCreateChange : setLocalShowCreate
  const [editing, setEditing] = useState<CmdbHandler | undefined>()
  const [confirmDelete, setConfirmDelete] = useState<CmdbHandler | undefined>()

  const { data: resp, isLoading } = useQuery({
    queryKey: ['cmdb-handlers', orgId],
    queryFn: () => cmdbApi.listHandlers(orgId),
    enabled: !!orgId,
  })

  const deleteMutation = useMutation({
    mutationFn: (cid: string) => cmdbApi.deleteHandler(orgId, cid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cmdb-handlers', orgId] })
      setConfirmDelete(undefined)
    },
  })

  const handlers = resp ?? []

  return (
    <div className={asTab ? 'space-y-6' : 'p-6 max-w-6xl mx-auto space-y-6'}>
      {!asTab && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-text-light dark:text-text-dark">CMDB Handlers</h1>
            <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
              Bidirectional sync between Quantizant inventories and your CMDB.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/cmdb/mapping-profiles"
              className="px-3 py-2 text-sm font-medium rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-primary/5 transition-colors"
            >
              Inbound Profiles
            </Link>
            <Link
              to="/cmdb/outbound-profiles"
              className="px-3 py-2 text-sm font-medium rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-primary/5 transition-colors"
            >
              Outbound Profiles
            </Link>
            <Link
              to="/cmdb/import-export"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-primary/5 transition-colors"
            >
              <span className="material-icons text-base">swap_horiz</span>
              Import / Export
            </Link>
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
            >
              <span className="material-icons text-base">add</span>
              New Handler
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <span className="material-icons text-subtle-light dark:text-subtle-dark animate-spin text-2xl">sync</span>
        </div>
      ) : handlers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
            <span className="material-icons text-primary text-2xl">hub</span>
          </div>
          <p className="text-sm font-medium text-text-light dark:text-text-dark">No handlers yet</p>
          <p className="text-xs text-subtle-light dark:text-subtle-dark mt-1 max-w-xs">
            Create a handler to start syncing PQC risk data with your CMDB.
          </p>
          <button
            onClick={() => setIsCreating(true)}
            className="mt-4 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
          >
            Create First Handler
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {handlers.map((c) => (
            <div
              key={c.id}
              className="bg-surface-light dark:bg-[#0d1b2a] rounded-2xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark p-5 flex flex-col gap-4"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-text-light dark:text-text-dark truncate">{c.name}</p>
                  {c.instance_url && (
                    <p className="text-xs text-subtle-light dark:text-subtle-dark truncate mt-0.5">{c.instance_url}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 shrink-0 justify-end">
                  {exchangeBadge(c.exchange_type)}
                  {statusBadge(c.status)}
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary/15 text-secondary-dark dark:text-secondary font-medium">
                    {c.direction}
                  </span>
                  {c.capabilities_cache && !c.capabilities_cache.supports_writeback && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 font-medium">
                      inbound only
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs text-subtle-light dark:text-subtle-dark">
                <div>
                  <span className="block">Auth</span>
                  <span className="text-text-light dark:text-text-dark font-medium">{c.auth_type}</span>
                </div>
                <div>
                  <span className="block">Poll</span>
                  <span className="text-text-light dark:text-text-dark font-medium">{c.poll_interval_minutes}m</span>
                </div>
              </div>

              {c.last_health_check_at && (
                <p className="text-xs text-subtle-light dark:text-subtle-dark">
                  Health checked {new Date(c.last_health_check_at).toLocaleString()}
                </p>
              )}

              <div className="flex items-center gap-2 pt-1 border-t border-border-light dark:border-border-dark">
                <Link
                  to={`/cmdb/handlers/${c.id}`}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary/5 text-primary hover:bg-primary/10 transition-colors"
                >
                  <span className="material-icons text-sm">open_in_new</span>
                  Details
                </Link>
                <button
                  onClick={() => setEditing(c)}
                  className="p-1.5 rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-primary/5 hover:text-text-light dark:hover:text-text-dark transition-colors"
                >
                  <span className="material-icons text-base">edit</span>
                </button>
                <button
                  onClick={() => setConfirmDelete(c)}
                  className="p-1.5 rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-error transition-colors"
                >
                  <span className="material-icons text-base">delete_outline</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {isCreating && <HandlerModal onClose={() => setIsCreating(false)} />}
      {editing && <HandlerModal handler={editing} onClose={() => setEditing(undefined)} />}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-surface-light dark:bg-[#0d1b2a] rounded-2xl shadow-float p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center shrink-0">
                <span className="material-icons text-error">warning</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-text-light dark:text-text-dark">Delete handler?</p>
                <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5">
                  "{confirmDelete.name}" — this will stop all syncs for this handler.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(undefined)}
                className="px-4 py-2 text-sm font-medium rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-primary/5 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(confirmDelete.id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-error text-white hover:bg-error/90 disabled:opacity-50 transition-colors"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
